import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import type { CSSProperties } from 'styled-components';
import styled from 'styled-components';

import { PlayerFieldStatus } from '@sorare/core/src/__generated__/globalTypes';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LabelS } from '@sorare/core/src/atoms/typography';
import { Score } from '@sorare/core/src/components/scoring/Score';
import { StatIcon } from '@sorare/core/src/components/stats/StatIcon';
import { PLAYER_PLACEHOLDER } from '@sorare/core/src/constants/assets';
import { range } from '@sorare/core/src/lib/arrays';
import { getRarityColor } from '@sorare/core/src/lib/colors';
import { isType } from '@sorare/core/src/lib/gql';
import {
  negativeDecisiveStatKeys,
  positiveDecisiveStatKeys,
} from '@sorare/core/src/lib/stats';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import { SubIcon } from '@sorare/marketplace/src/shared/stats/SubIcon';
import { getDecisiveActions } from '@sorare/marketplace/src/shared/stats/lib';

import type {
  Player_anyPlayer,
  Player_anyPlayerInterfaceSubscription,
} from './__generated__/index.graphql.ts';

const Root = styled(Vertical).attrs({ gap: 0, center: true })`
  position: relative;
  isolation: isolate;
`;
const PlayerImage = styled.div`
  position: relative;
  width: var(--quadruple-unit);
  height: var(--quadruple-unit);
  border-radius: 100%;
  border: 1px solid var(--border-color);
  background-position: center top;
  background-size: contain;
  background-color: var(--c-nd-50-opaque);
  background-repeat: no-repeat;
  background-origin: padding-box;
  @media ${tabletAndAbove} {
    width: 44px;
    height: 44px;
  }
`;
const PlayerScoreWrapper = styled.div`
  z-index: 1;
  margin: calc(-1 * var(--unit)) auto 0 auto;
`;
const PlayerName = styled(LabelS)`
  background: rgba(var(--c-rgb-black), 0.3);
  border-radius: var(--unit);
  color: var(--c-white);
  padding: var(--half-unit) var(--unit);
  text-align: center;

  & > *:first-child {
    color: var(--c-nd-600);
    margin-right: var(--half-unit);
  }

  &.highlighted {
    background: var(--c-onBase-success);
    color: var(--c-black);

    & > *:first-child {
      color: var(--c-nl-600);
    }
  }
`;

const SubWrapper = styled(Horizontal)`
  position: absolute;
  gap: var(--half-unit);
  font-weight: var(--t-bold);
  right: 80%;
  top: -5px;
  font-size: 10px;
  color: var(--c-white);
`;
const DecisiveAction = styled(Vertical).attrs({ gap: 0, center: true })`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
`;
const NegativeDecisiveActions = styled(DecisiveAction)`
  right: calc(100% - var(--half-unit));
`;
const PositiveDecisiveActions = styled(DecisiveAction)`
  left: calc(100% - var(--half-unit));
`;
const StyledStatIcon = styled(StatIcon)`
  &:not(:first-child) {
    margin-top: -6px;
  }
`;
const MinPlayedWrapper = styled.div`
  opacity: 0.6;
`;
const CardWrapper = styled(Vertical).attrs({ gap: 0, center: true })`
  position: absolute;
  right: calc(100% - var(--half-unit));
  top: calc(50% - var(--unit));
  transform: rotateZ(4deg);

  > *:not(:last-child) {
    margin-right: -4px;
  }
`;

type Props = {
  player: Player_anyPlayer;
  highlighted?: boolean;
  highlightInLineupPlayers?: boolean;
  startedBench?: boolean;
};
export const Player = ({
  player,
  highlighted,
  highlightInLineupPlayers,
  startedBench,
}: Props) => {
  const { playerGameScore } = player;

  if (!playerGameScore || !isType(playerGameScore, 'PlayerGameScore')) {
    return null;
  }

  const { anyPlayerGameStats } = playerGameScore;

  const decisiveActions = getDecisiveActions(playerGameScore);

  const playerMinutesPlayed =
    (isType(anyPlayerGameStats, 'PlayerGameStats') &&
      anyPlayerGameStats.minsPlayed) ||
    0;

  const fieldStatus =
    isType(anyPlayerGameStats, 'PlayerGameStats') &&
    anyPlayerGameStats.fieldStatus;

  const substituted = fieldStatus === PlayerFieldStatus.SUBSTITUTED;
  const substitutee = fieldStatus === PlayerFieldStatus.ON_FIELD;

  const avatarPictureUrl =
    (highlightInLineupPlayers && anyPlayerGameStats.myBestSo5Appearance
      ? anyPlayerGameStats.myBestSo5Appearance.pictureUrl
      : player.avatarPictureUrl) || PLAYER_PLACEHOLDER;

  const baseBorderColor = highlighted
    ? 'var(--c-onBase-success)'
    : 'var(--c-nd-150)';
  const borderColor =
    highlightInLineupPlayers && anyPlayerGameStats.myBestSo5Appearance
      ? getRarityColor(anyPlayerGameStats.myBestSo5Appearance.rarity)
      : baseBorderColor;

  return (
    <Root>
      <PlayerImage
        style={
          {
            '--border-color': borderColor,
            backgroundImage: `url(${avatarPictureUrl})`,
          } as CSSProperties
        }
      >
        {!!startedBench && !!substitutee && (
          <SubWrapper>
            <MinPlayedWrapper>{playerMinutesPlayed}&apos;</MinPlayedWrapper>
            <SubIcon type="sub_in" />
          </SubWrapper>
        )}
        {!!substituted && (
          <SubWrapper>
            <MinPlayedWrapper>{playerMinutesPlayed}&apos;</MinPlayedWrapper>
            <SubIcon type="sub_out" />
          </SubWrapper>
        )}
        {decisiveActions.yellow_card > 0 && (
          <CardWrapper>
            <StatIcon statKey="yellow_card" />
          </CardWrapper>
        )}
        {decisiveActions.red_card > 0 && (
          <CardWrapper>
            <StatIcon statKey="red_card" />
          </CardWrapper>
        )}
        <NegativeDecisiveActions>
          {negativeDecisiveStatKeys.map(statKey =>
            range(decisiveActions[statKey] || 0).map(i => (
              <StyledStatIcon key={`${statKey}-${i}`} statKey={statKey} />
            ))
          )}
        </NegativeDecisiveActions>
        <PositiveDecisiveActions>
          {positiveDecisiveStatKeys.map(statKey =>
            range(decisiveActions[statKey] || 0).map(i => (
              <StyledStatIcon key={`${statKey}-${i}`} statKey={statKey} />
            ))
          )}
        </PositiveDecisiveActions>
      </PlayerImage>
      <PlayerScoreWrapper>
        <Score
          hideReviewing
          shape="PILL"
          score={playerGameScore.score || 0}
          scoreStatus={playerGameScore.scoreStatus}
          scoreType="PLAYER_SCORE"
          fieldStatus={fieldStatus || undefined}
          size={3}
        />
      </PlayerScoreWrapper>
      <PlayerName className={classNames({ highlighted })}>
        <span>{player.shirtNumber}</span>
        <strong>{player.matchName}</strong>
      </PlayerName>
    </Root>
  );
};

Player.fragments = {
  anyPlayer: gql`
    fragment Player_anyPlayer on AnyPlayerInterface {
      slug
      matchName
      avatarPictureUrl
      shirtNumber
      playerGameScore(gameId: $id) {
        id
        score
        scoreStatus
        anyPlayerGameStats {
          id
          myBestSo5Appearance {
            id
            rarity
            pictureUrl(derivative: "avatar")
          }
          ... on PlayerGameStats {
            id
            fieldStatus
            minsPlayed
          }
        }
        ...getDecisiveActions_playerGameScoreInterface
      }
    }
    ${getDecisiveActions.fragments.playerGameScoreInterface}
  ` as TypedDocumentNode<Player_anyPlayer>,
  subscriptions: {
    // Do not add myBestSo5Appearance here, as subscription isn't user specific. It will return random results.
    anyPlayerInterface: gql`
      fragment Player_anyPlayerInterfaceSubscription on AnyPlayerInterface {
        slug
        playerGameScore(gameId: $id) {
          id
          score
          scoreStatus
          anyPlayerGameStats {
            id
            ... on PlayerGameStats {
              id
              fieldStatus
              minsPlayed
            }
          }
          ...getDecisiveActions_playerGameScoreInterface
        }
      }
      ${getDecisiveActions.fragments.playerGameScoreInterface}
    ` as TypedDocumentNode<Player_anyPlayerInterfaceSubscription>,
  },
};
